import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useAuth } from './use-auth';
import {
  getReferenceDataService,
  type IReferenceBand,
  type IReferenceDataBundle,
  type IReferenceItem,
  type ReferenceBandKind,
} from '../services/reference-data';

/** Cache window. Reference data changes only when an admin edits it. */
const STALE_TIME_MS = 5 * 60 * 1000;

export type ReferenceKind =
  | 'industry'
  | 'businessType'
  | 'consumerGroup'
  | ReferenceBandKind;

/**
 * Resolves the display string for a reference row.
 *
 * The API returns English labels as the source of truth. A translation is used
 * when one exists, and the stored label is the fallback, so a row an admin adds
 * tomorrow renders correctly today without a code change. This is also why the
 * *code* is what gets stored on the business: the previous implementation wrote
 * the translated label into the column, so the same click produced a different
 * database value in English and Bahasa Indonesia.
 */
export const referenceLabel = (
  t: TFunction,
  kind: ReferenceKind,
  item: { code: string; label?: string | null }
): string =>
  t(`reference.${kind}.${item.code}`, {
    defaultValue: item.label ?? item.code,
  });

export interface ReferenceOption {
  value: string;
  label: string;
}

export const toOptions = (
  t: TFunction,
  kind: ReferenceKind,
  items: Array<{ code: string; label?: string | null }> | undefined
): ReferenceOption[] =>
  (items ?? []).map((item) => ({
    value: item.code,
    label: referenceLabel(t, kind, item),
  }));

/**
 * @param countryCode narrows the legal entity forms to that country's plus the
 * globally available ones. Pass the country the user has selected so a
 * Singaporean sees "Pte Ltd" and an Indian sees "OPC".
 */
export const useReferenceData = (countryCode?: string) => {
  const { accessToken } = useAuth();

  const query = useQuery<IReferenceDataBundle | null>({
    queryKey: ['reference-data', countryCode ?? 'all'],
    queryFn: () =>
      getReferenceDataService(countryCode, accessToken ?? undefined),
    staleTime: STALE_TIME_MS,
    enabled: !!accessToken,
  });

  const data = query.data ?? null;

  return useMemo(
    () => ({
      ...query,
      data,
      industries: data?.industries ?? [],
      businessTypes: data?.businessTypes ?? [],
      consumerGroups: data?.consumerGroups ?? [],
      currencies: data?.currencies ?? [],
      bands: data?.bands,
      termsVersion: data?.termsVersion ?? null,
    }),
    [query, data]
  );
};

/**
 * Formats a band for display in the viewer's currency.
 *
 * Money bands are stored once, in USD minor units. The old onboarding form
 * converted with a fixed `usdAmount * 15000` for IDR; the rate passed in here
 * comes from the exchange-rate service the API already runs, so the numbers are
 * current and every currency works, not just two.
 *
 * @param usdPerUnit how many units of the display currency one USD buys. Pass 1
 * to render in USD.
 */
export const formatBandLabel = (
  band: IReferenceBand,
  currency: string,
  usdPerUnit: number,
  locale: string
): string => {
  const isCount = band.kind === 'store_count';

  const toNumber = (value: string | number | null | undefined) =>
    value === null || value === undefined ? null : Number(value);

  const min = toNumber(band.rangeMin);
  const max = toNumber(band.rangeMax);

  const render = (minorUsd: number): string => {
    if (isCount) return String(minorUsd);
    const amount = (minorUsd / 100) * usdPerUnit;
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isCount) {
    if (min === 0 && max === 0) return band.label ?? '0';
    if (min !== null && max === null) return `${min}+`;
    if (min !== null && max !== null && min === max) return String(min);
    if (min !== null && max !== null) return `${min}-${max}`;
    return band.label ?? band.code;
  }

  if (min === 0 && max === 0) return band.label ?? render(0);
  if (min === null && max !== null) return `< ${render(max)}`;
  if (min !== null && max === null) return `> ${render(min)}`;
  if (min !== null && max !== null) return `${render(min)} - ${render(max)}`;

  return band.label ?? band.code;
};

export type { IReferenceBand, IReferenceItem };
